import { X } from "lucide-react";
import { MetaAdPreview } from "./MetaAdPreview";
import { TikTokAdPreview } from "./TikTokAdPreview";
import { PlatformBadge } from "./PlatformBadge";
import type { UnifiedAd, UnifiedCampaign, UnifiedAdGroup } from "./types";

type DetailItem = UnifiedCampaign | UnifiedAdGroup | UnifiedAd;

interface DetailPanelProps {
  item: DetailItem;
  itemType: "campaign" | "adgroup" | "ad";
  onClose: () => void;
}

function formatCurrency(v: number): string {
  return "$" + v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function MetricRow({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-gray-50">
      <span className="text-xs text-gray-500">{label}</span>
      <span className="text-xs font-medium text-gray-900">{value}</span>
    </div>
  );
}

export function DetailPanel({ item, itemType, onClose }: DetailPanelProps) {
  const platformSlug = item.platformSlug;
  const metrics = item.metrics;
  const isAd = itemType === "ad";
  const ad = isAd ? (item as UnifiedAd) : null;

  return (
    <div className="fixed inset-y-0 right-0 w-[480px] bg-white shadow-2xl border-l border-gray-200 z-50 flex flex-col" data-testid="detail-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <PlatformBadge slug={platformSlug} size="md" />
          <div>
            <h3 className="text-sm font-semibold text-gray-900 truncate max-w-[360px]">{item.name}</h3>
            <p className="text-xs text-gray-500 capitalize">{itemType} · {item.status}</p>
          </div>
        </div>
        <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded" data-testid="button-close-detail">
          <X className="w-4 h-4 text-gray-500" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="p-4">
          <h4 className="text-xs font-semibold text-gray-400 uppercase mb-2">Performance (Last 30 Days)</h4>
          <MetricRow label="Spend" value={formatCurrency(metrics.spend)} />
          <MetricRow label="Impressions" value={metrics.impressions.toLocaleString()} />
          <MetricRow label="Clicks" value={metrics.clicks.toLocaleString()} />
          <MetricRow label="CTR" value={metrics.ctr + "%"} />
          <MetricRow label="CPC" value={formatCurrency(metrics.cpc)} />
          <MetricRow label="CPM" value={formatCurrency(metrics.cpm)} />
          <MetricRow label="Reach" value={metrics.reach.toLocaleString()} />
          <MetricRow label="Conversions" value={metrics.conversions.toLocaleString()} />
          <MetricRow label="ROAS" value={metrics.roas + "x"} />
          <MetricRow label="Revenue" value={formatCurrency(metrics.purchaseValue)} />
          <MetricRow label="Cost per Result" value={metrics.costPerResult > 0 ? formatCurrency(metrics.costPerResult) : "—"} />

          {platformSlug === "meta" && (
            <>
              <h4 className="text-xs font-semibold text-gray-400 uppercase mt-4 mb-2">Meta Metrics</h4>
              <MetricRow label="Quality Ranking" value="—" />
              <MetricRow label="Engagement Ranking" value="—" />
              <MetricRow label="Outbound Clicks" value={metrics.clicks.toLocaleString()} />
            </>
          )}

          {platformSlug === "tiktok" && (
            <>
              <h4 className="text-xs font-semibold text-gray-400 uppercase mt-4 mb-2">TikTok Metrics</h4>
              <MetricRow label="Video Views" value={(metrics.videoViews || 0).toLocaleString()} />
              <MetricRow label="6s Views" value={(metrics.sixSecondViews || 0).toLocaleString()} />
              <MetricRow label="Completion Rate" value={metrics.videoViews ? ((metrics.videoCompletions || 0) / metrics.videoViews * 100).toFixed(1) + "%" : "—"} />
              <MetricRow label="Profile Visits" value={(metrics.profileVisits || 0).toLocaleString()} />
              <MetricRow label="Likes" value={(metrics.likes || 0).toLocaleString()} />
              <MetricRow label="Comments" value={(metrics.comments || 0).toLocaleString()} />
              <MetricRow label="Shares" value={(metrics.shares || 0).toLocaleString()} />
              <MetricRow label="Saves" value={(metrics.saves || 0).toLocaleString()} />
            </>
          )}
        </div>

        {"objective" in item && item.objective && (
          <div className="px-4 pb-4">
            <h4 className="text-xs font-semibold text-gray-400 uppercase mb-2">Details</h4>
            <MetricRow label="Objective" value={item.objective} />
            {"dailyBudget" in item && item.dailyBudget && (
              <MetricRow label="Daily Budget" value={formatCurrency(item.dailyBudget)} />
            )}
            {"lifetimeBudget" in item && item.lifetimeBudget && (
              <MetricRow label="Lifetime Budget" value={formatCurrency(item.lifetimeBudget)} />
            )}
          </div>
        )}

        {"targetingSummary" in item && item.targetingSummary && (
          <div className="px-4 pb-4">
            <h4 className="text-xs font-semibold text-gray-400 uppercase mb-2">Targeting</h4>
            <p className="text-xs text-gray-700">{item.targetingSummary}</p>
          </div>
        )}

        {isAd && ad && (
          <div className="px-4 pb-4">
            <h4 className="text-xs font-semibold text-gray-400 uppercase mb-3">Ad Preview</h4>
            <div className="flex justify-center">
              {platformSlug === "tiktok" ? (
                <TikTokAdPreview
                  headline={ad.headline}
                  body={ad.body}
                  imageUrl={ad.imageUrl}
                  thumbnailUrl={ad.thumbnailUrl}
                  creativeType={ad.creativeType}
                  tiktokCreative={ad.tiktokCreative}
                  metrics={{
                    likes: metrics.likes || 0,
                    comments: metrics.comments || 0,
                    shares: metrics.shares || 0,
                    saves: metrics.saves || 0,
                  }}
                />
              ) : (
                <MetaAdPreview
                  headline={ad.headline}
                  body={ad.body}
                  callToAction={ad.callToAction}
                  imageUrl={ad.imageUrl}
                  landingPageUrl={ad.landingPageUrl}
                  creativeType={ad.creativeType}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
